import {DomainStore} from 'shared/store';
import { makeAutoObservable } from 'mobx';
import {t, api} from 'shared/core';
import {endpoints, types} from 'main/tools';
import {successAlert} from 'shared/tools';
import {NmbrPaySchedule} from 'main/stores/nmbr';
import _ from 'lodash';

class NmbrEmployeesDirectoryState {
  interactiveAgent;
  store = new DomainStore();
  pollingInterval = null;

  paySchedules = [];
  selectedBusinessEntityId = null;

  selectedEmployee;
  selectedEmployees;
  selectedPayScheduleId;
  addInProgress = false;
  batchAddResult;
  batchAddId;
  errors = {};

  constructor() {
    makeAutoObservable(this, {
      interactiveAgent: false,
      store: false
    });
  }

  async load() {
    await this.store._compose([
      endpoints.NMBR.PAY_SCHEDULES.ALL
    ]);

    this.paySchedules = this.store._getAll(
      types.NMBR.PAY_SCHEDULE,
      NmbrPaySchedule
    );

    if (this.businessEntities.length === 1) {
      this.selectedBusinessEntityId = this.businessEntities[0].id;
    }
  }

  get businessEntities() {
    return _.uniqBy(
      this.paySchedules.map(ps => ({
        id: ps.businessEntityId,
        name: ps.businessEntityName
      })),
      'id'
    );
  }

  get selectablePaySchedules() {
    if (!this.selectedBusinessEntityId) return [];

    return this.paySchedules.filter(ps => ps.businessEntityId === this.selectedBusinessEntityId);
  }

  onBusinessEntityChanged(businessEntityId) {
    this.selectedBusinessEntityId = businessEntityId;
    this.selectedPayScheduleId = null;
  }

  setInteractiveAgent(agent) {
    this.interactiveAgent = agent;
  }

  async addEmployee() {
    const {model, errors} = await this.store.post(
      `${this.selectedEmployee.link('addToPayroll')}${this.selectedPayScheduleId ? `?nmbr_pay_schedule_id=${this.selectedPayScheduleId}` : ''}`,
      types.EMPLOYEE
    );

    this.errors = errors;
    if (model) {
      this.closeAddEmployeeModal();
      successAlert(t('nmbr.employees.directory.EMPLOYEE_ADDED', { name: this.selectedEmployee.name }));
      this.interactiveAgent.refresh();

      if (!_.isEmpty(model.errorsFromLastSync)) {
        this.openEmployeeErroredModal(model);
      }
    }
  }

  updateSelectedPayScheduleId(payScheduleId) {
    this.selectedPayScheduleId = payScheduleId;
  }

  async performBatchAdd() {
    this.addInProgress = true;

    try {
      const {data} = await api.post(
        endpoints.NMBR.EMPLOYEES.BATCH_ADD_EMPLOYEES,
        {
          employeeIds: _.map(this.selectedEmployees, 'id'),
          nmbrPayScheduleId: this.selectedPayScheduleId,
          nmbrBusinessEntityId: this.selectedBusinessEntityId
        }
      );

      this.batchAddId = data.data.id;
      this.pollBatchAddStatus();
    } catch (error) {
      this.errors = error.formErrors;
    }
  }

  pollBatchAddStatus() {
    this.pollingInterval = setInterval(async () => {
      const {data} = await api.get(
        endpoints.NMBR.EMPLOYEES.BATCH_ADD_STATUS.with(this.batchAddId)
      );

      if (data.data.attributes.status === 'completed' || data.data.attributes.status === 'failed') {
        this.clearPolling();
        this.batchAddResult = data.data.attributes.result;
        this.interactiveAgent.refresh();
        this.addInProgress = false;
      }
    }, 2000);
  }

  clearPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  async batchAddEmployees() {
    this.errors = {};

    await this.performBatchAdd();

    if (_.isEmpty(this.errors)) {
      this.closeBatchAddEmployeesModal();
      this.openBatchAddEmployeesResultsModal();
    }
  }

  closeBatchAddEmployeesResultsModal() {
    this.clearPolling();
    this.batchAddResult = null;
    this.addInProgress = false;
  }

  async removeEmployee() {
    const {model} = await this.store.post(
      this.selectedEmployee.link('removeFromPayroll'),
      types.EMPLOYEE
    );

    if (model) {
      successAlert(t('nmbr.employees.directory.EMPLOYEE_REMOVED', { name: this.selectedEmployee.name }));
      this.closeRemoveEmployeeModal();
      this.interactiveAgent.refresh();
    }
  }

  get addedEmployees() {
    return _.filter(this.batchAddResult, employee => employee.status === 'success');
  }

  get erroredEmployees() {
    return _.filter(this.batchAddResult, employee => employee.status !== 'success');
  }

  customLinksFor(employee) {
    const links = [];

    if (employee.hasLink('addToPayroll')) {
      links.push({
        order: 0,
        text: 'nmbr.employees.directory.Add to payroll',
        action: () => this.openAddEmployeeModal(employee)
      });
    }

    if (employee.hasLink('removeFromPayroll')) {
      links.push({
        order: 0,
        text: 'nmbr.employees.directory.Remove from payroll',
        action: () => this.openRemoveEmployeeModal(employee)
      });
    }

    return links;
  }
}

export default NmbrEmployeesDirectoryState;
